import { Link, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import TryItOutDemo from "@/components/TryItOutDemo";
import {
  MagnifyingGlass, Sparkle, PaperPlaneTilt, ArrowRight, ShieldCheck,
  Buildings, ChartLineUp, FileText, Lightning, Star, At, CheckCircle, Play,
} from "@phosphor-icons/react";

const STATS = [
  { value: "$1.2T+", label: "Federal grant opportunities indexed" },
  { value: "Live", label: "Grants.gov real-time sync" },
  { value: "Claude 4.5", label: "AI proposal authoring" },
  { value: "<5 min", label: "Search → submitted draft" },
];

const FEATURES = [
  {
    icon: MagnifyingGlass,
    over: "01 — DISCOVER",
    title: "Live Grants.gov search",
    body: "Filter $1T+ in federal opportunities by keyword, agency, status, and close date. Save searches, get email digests when matches post.",
    bullet: ["Smart filter chips", "Saved-search alerts (daily / weekly / immediate)", "One email per finding or rolled-up summary"],
  },
  {
    icon: Sparkle,
    over: "02 — DRAFT",
    title: "AI-authored proposals",
    body: "Generate a structured, agency-aligned draft in seconds with Claude Sonnet 4.5 — narrative, objectives, evaluation plan, budget rationale.",
    bullet: ["Editable Markdown source", "Watermarked PDF export", "Template marketplace"],
  },
  {
    icon: PaperPlaneTilt,
    over: "03 — SUBMIT",
    title: "Email-to-agency submission",
    body: "One-click submission to a government contact via verified email — with tracking, outcome reporting, and Stripe-receipted billing.",
    bullet: ["DKIM-signed sending domain", "Outcome tracking", "Approval-rate reporting"],
  },
];

const STEPS = [
  { n: "01", title: "Search", body: "Type a keyword. Pick filters. Save the search if you'll repeat it weekly." },
  { n: "02", title: "Generate", body: "Open any opportunity. Click Generate. Claude drafts a full proposal in your org's voice." },
  { n: "03", title: "Submit", body: "Edit if needed. Click Submit. The PDF is emailed to the agency contact, watermarked and tracked." },
];

export default function Landing() {
  const { user } = useAuth();
  const [demoOpen, setDemoOpen] = useState(false);
  const [founder, setFounder] = useState(null);
  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/founder/public-status`;
    axios.get(url).then((r) => setFounder(r.data)).catch(() => {});
  }, []);
  if (user) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 gf-sharp" data-testid="landing-page">
      {demoOpen && <TryItOutDemo onClose={() => setDemoOpen(false)} />}      {/* ============================== Sticky Nav ============================== */}
      <nav className="sticky top-0 z-40 border-b border-zinc-200 bg-white/90 backdrop-blur" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
            <div className="h-8 w-8 bg-blue-700 flex items-center justify-center">
              <Buildings size={18} className="text-white" weight="bold" />
            </div>
            <div>
              <div className="font-display font-black text-lg leading-none tracking-tight">GrantFlow</div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">Federal Grants Suite</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="nav-login" className="hidden sm:inline-block text-xs font-bold uppercase tracking-widest text-zinc-700 hover:text-zinc-900 px-4 py-2">
              Sign in
            </Link>
            <Link to="/register" data-testid="nav-register" className="bg-blue-700 hover:bg-blue-800 text-white text-xs font-bold uppercase tracking-widest px-5 py-2.5">
              Start free trial
            </Link>
          </div>
        </div>
      </nav>

      {/* ================================ Hero ================================ */}
      <section className="gf-grid-bg border-b border-zinc-200" data-testid="hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <div className="gf-overline">A federal grants suite · Built 2026</div>
              <div className="mt-4 font-mono text-sm lg:text-base font-bold uppercase tracking-[0.3em] text-zinc-900" data-testid="hero-slogan">
                Search<span className="text-blue-700">.</span> Draft<span className="text-blue-700">.</span> Submit<span className="text-blue-700">.</span>
              </div>
              <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter mt-6">
                Find a grant.<br />
                <span className="text-blue-700">Draft a proposal.</span><br />
                Submit before close.
              </h1>
              <p className="mt-8 text-lg lg:text-xl text-zinc-600 max-w-2xl leading-relaxed">
                GrantFlow is the workspace federal-grant teams use to discover opportunities on
                Grants.gov, draft compliant proposals with Claude Sonnet 4.5, and send them
                straight to the agency contact — all from one keyboard.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Link to="/register" data-testid="hero-cta-primary" className="group bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-widest px-8 py-4 inline-flex items-center justify-center gap-2 text-sm">
                  Start 7-day free trial
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" weight="bold" />
                </Link>
                <button
                  type="button"
                  onClick={() => setDemoOpen(true)}
                  data-testid="hero-cta-try-demo"
                  className="group bg-amber-400 hover:bg-amber-500 text-zinc-900 font-bold uppercase tracking-widest px-8 py-4 inline-flex items-center justify-center gap-2 text-sm border-2 border-zinc-900 shadow-[4px_4px_0_0_rgba(24,24,27,1)] hover:shadow-[2px_2px_0_0_rgba(24,24,27,1)] hover:translate-x-0.5 hover:translate-y-0.5 transition-all"
                >
                  <Play size={14} weight="fill" /> Try it out
                </button>
                <Link to="/login" data-testid="hero-cta-secondary" className="border-2 border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white font-bold uppercase tracking-widest px-8 py-4 inline-flex items-center justify-center text-sm transition-colors">
                  Sign in
                </Link>
              </div>
              <div className="mt-8 text-xs font-mono text-zinc-500 flex items-center gap-2">
                <ShieldCheck size={14} />
                No credit card · Cancel anytime · DKIM-verified email delivery
              </div>
            </div>

            {/* Right rail — workflow mock (no code/terminal styling) */}
            <div className="lg:col-span-4 lg:sticky lg:top-24">
              <div className="border-2 border-zinc-900 bg-white shadow-[8px_8px_0_0_rgba(24,24,27,1)]">
                <div className="border-b border-zinc-200 px-4 py-3 flex items-center gap-3 bg-zinc-50">
                  <div className="h-7 w-7 bg-blue-700 flex items-center justify-center">
                    <Buildings size={14} className="text-white" weight="bold" />
                  </div>
                  <div>
                    <div className="text-xs font-bold tracking-tight">GrantFlow</div>
                    <div className="text-[10px] text-zinc-500 font-mono">Live workflow</div>
                  </div>
                </div>

                <div className="p-5 space-y-4">
                  {/* Step 1 — Search */}
                  <div className="border-l-2 border-blue-700 pl-3">
                    <div className="text-[10px] uppercase tracking-widest text-blue-700 font-bold">Step 1 · Search</div>
                    <div className="mt-1 text-sm text-zinc-900 font-semibold">"Rural broadband"</div>
                    <div className="mt-1 text-xs text-zinc-500">47 grants · USDA · NTIA · FCC</div>
                  </div>

                  {/* Step 2 — Draft */}
                  <div className="border-l-2 border-blue-700 pl-3">
                    <div className="text-[10px] uppercase tracking-widest text-blue-700 font-bold">Step 2 · Draft with AI</div>
                    <div className="mt-1 text-sm text-zinc-900 font-semibold flex items-center gap-2">
                      <span className="inline-block h-2 w-2 bg-blue-700 animate-pulse rounded-full" />
                      Claude 4.5 drafting USDA-RBD-25…
                    </div>
                    <div className="mt-1 text-xs text-zinc-500">9 sections · 1,200 words</div>
                  </div>

                  {/* Step 3 — Submit */}
                  <div className="border-l-2 border-emerald-700 pl-3">
                    <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Step 3 · Submitted</div>
                    <div className="mt-1 text-sm text-zinc-900 font-semibold flex items-center gap-2">
                      <CheckCircle size={14} weight="fill" className="text-emerald-600" />
                      Sent to agency contact
                    </div>
                    <div className="mt-1 text-xs text-zinc-500">Receipt logged · Outcome tracking on</div>
                  </div>
                </div>
              </div>
              <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-zinc-500 text-right">
                ★ Workflow demo
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =============================== Pricing peek (slim strip) =========
          Puts pricing "at the top" but tastefully — one line per tier, no
          feature bullets, no scarcity theater. Click any tile to jump to
          the full comparison table further down. */}
      <section className="border-b border-zinc-200 bg-white" data-testid="pricing-peek">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <div className="gf-overline">Pricing at a glance</div>
              <div className="text-xs text-zinc-500 mt-1 font-mono">
                Every plan unlocks every feature.
              </div>
            </div>
            <a
              href="#pricing"
              data-testid="pricing-peek-see-details"
              className="hidden sm:inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-blue-700 hover:text-blue-900"
            >
              See what&apos;s included ↓
            </a>
          </div>

          {/* Founder's Plan callout — full block treatment (same as the one
              in the full pricing section below). Auto-hides on sold-out. */}
          {founder && !founder.sold_out && (
            <div className="mb-6 border-2 border-amber-500 bg-white p-6 lg:p-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6" data-testid="founder-ribbon">
              <div className="flex items-start gap-4 flex-1">
                <div className="hidden sm:flex h-12 w-12 shrink-0 bg-amber-500 items-center justify-center">
                  <Star size={22} weight="fill" className="text-zinc-900" />
                </div>
                <div>
                  <div className="text-[11px] font-mono uppercase tracking-widest text-amber-600">
                    Founder&apos;s Plan · Locked for life
                  </div>
                  <div className="font-display font-black text-3xl lg:text-4xl tracking-tight mt-1">
                    <span className="text-xl lg:text-2xl text-zinc-400 line-through font-bold mr-2" data-testid="founder-ribbon-old-price">$9.99</span>
                    ${founder.founder_price_usd.toFixed(2)}
                    <span className="text-base text-zinc-500 ml-1 font-normal">/month, forever</span>
                  </div>
                  <p className="text-sm text-zinc-600 mt-2 max-w-md">
                    A thank-you for backing GrantFlow in year one. The <b>${founder.founder_price_usd.toFixed(2)}/mo</b> rate never
                    increases for as long as you stay subscribed. Available to the first{" "}
                    <b>{founder.founder_limit}</b> subscribers only.
                    {founder.lifetime_price_usd ? (
                      <span className="block mt-1">
                        Prefer one payment? <b>${founder.lifetime_price_usd.toFixed(0)} once</b> = lifetime access, same first-100 pool.
                      </span>
                    ) : null}
                  </p>
                </div>
              </div>
              <div className="w-full lg:w-auto flex flex-col items-start lg:items-end gap-2">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                  Spots remaining
                </div>
                <div className="font-display font-black text-4xl tracking-tight text-amber-600">
                  {founder.founder_remaining}<span className="text-lg text-zinc-400 font-normal">/{founder.founder_limit}</span>
                </div>
                <Link
                  to="/register"
                  data-testid="founder-ribbon-cta"
                  className="bg-amber-500 hover:bg-amber-600 text-zinc-900 font-bold uppercase tracking-widest px-6 py-3 text-xs inline-flex items-center gap-2"
                >
                  Claim founder pricing
                  <ArrowRight size={12} weight="bold" />
                </Link>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
            {[
              { id: "peek-trial", label: "Trial", price: "Free", sub: "7 days · No card", cta: "Start", bg: "bg-white", accent: "text-zinc-900" },
              { id: "peek-monthly", label: "Monthly", price: "$14.99", sub: "per month · Cancel anytime", cta: "Subscribe", bg: "bg-white", accent: "text-blue-700", badge: "★ Recommended" },
              { id: "peek-pass", label: "30-Day Pass", price: "$19.99", sub: "One-time · No renewal", cta: "Buy pass", bg: "bg-white", accent: "text-zinc-900" },
              { id: "peek-lifetime", label: "Lifetime", price: "$199", sub: "One-time · Never expires", cta: "Buy lifetime", bg: "bg-zinc-900", accent: "text-amber-400", badge: "Best value" },
            ].map((t) => (
              <a
                key={t.id}
                href="#pricing"
                data-testid={t.id}
                className={`${t.bg} p-5 lg:p-6 group hover:opacity-90 transition-opacity relative ${t.bg === "bg-zinc-900" ? "text-white" : "text-zinc-900"}`}
              >
                {t.badge && (
                  <div className={`absolute top-0 right-0 text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 ${
                    t.badge.includes("Best") ? "bg-amber-400 text-zinc-900" : "bg-blue-700 text-white"
                  }`}>
                    {t.badge}
                  </div>
                )}
                <div className={`text-[10px] font-mono uppercase tracking-widest ${t.accent}`}>{t.label}</div>
                <div className="font-display font-black text-3xl lg:text-4xl tracking-tight mt-2">
                  {t.price}
                </div>
                <div className={`text-[11px] font-mono mt-1 ${t.bg === "bg-zinc-900" ? "text-zinc-400" : "text-zinc-500"}`}>
                  {t.sub}
                </div>
                <div className={`mt-3 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1 ${
                  t.bg === "bg-zinc-900" ? "text-amber-400 group-hover:text-amber-300" : "text-blue-700 group-hover:text-blue-900"
                }`}>
                  {t.cta} <ArrowRight size={11} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* =============================== Stats =============================== */}
      <section className="border-b border-zinc-200 bg-zinc-900 text-white" data-testid="stats-strip">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-zinc-700">
            {STATS.map((s) => (
              <div key={s.label} className="px-6 first:pl-0 last:pr-0 py-2">
                <div className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-blue-400">
                  {s.value}
                </div>
                <div className="text-xs font-mono uppercase tracking-widest text-zinc-400 mt-2">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ Features grid ============================ */}
      <section className="border-b border-zinc-200" data-testid="features">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-12 items-end mb-16">
            <div className="lg:col-span-7">
              <div className="gf-overline">Capabilities</div>
              <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4 leading-[0.95]">
                Three primitives.<br />
                <span className="text-zinc-500">One unfair advantage.</span>
              </h2>
            </div>
            <div className="lg:col-span-5 text-lg text-zinc-600 leading-relaxed">
              Most teams string together a search engine, a Word doc, and an outbox. GrantFlow
              collapses the workflow into three composable primitives that share state.
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
            {FEATURES.map((f) => (
              <div key={f.title} className="bg-white p-8 lg:p-10 hover:bg-zinc-50 transition-colors" data-testid={`feature-${f.over.split(" ")[0]}`}>
                <div className="h-12 w-12 bg-zinc-900 text-white flex items-center justify-center">
                  <f.icon size={22} weight="bold" />
                </div>
                <div className="gf-overline mt-6">{f.over}</div>
                <h3 className="font-display font-bold text-2xl tracking-tight mt-2">{f.title}</h3>
                <p className="text-zinc-600 mt-4 leading-relaxed">{f.body}</p>
                <ul className="mt-6 space-y-2">
                  {f.bullet.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-zinc-700">
                      <span className="font-mono text-blue-700 mt-0.5">→</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================== How it works =========================== */}
      <section className="border-b border-zinc-200 bg-zinc-50" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="gf-overline">Workflow</div>
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4 leading-[0.95] max-w-3xl">
            From cold start to submitted in <span className="text-blue-700">under 5 minutes</span>.
          </h2>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-16 mt-16">
            {STEPS.map((s, idx) => (
              <div key={s.n} className="relative" data-testid={`step-${s.n}`}>
                <div className="font-display font-black text-7xl lg:text-8xl text-zinc-200 leading-none tracking-tighter">
                  {s.n}
                </div>
                <div className="-mt-8 lg:-mt-12 relative">
                  <h3 className="font-display font-bold text-2xl tracking-tight">{s.title}</h3>
                  <p className="text-zinc-600 mt-3 leading-relaxed">{s.body}</p>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className="hidden md:block absolute top-12 right-[-2rem] text-zinc-300">
                    <ArrowRight size={28} weight="thin" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================= Collaboration ============================= */}
      <section className="border-b border-zinc-200" data-testid="collab">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="gf-overline">Multi-seat workspaces</div>
              <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-4 leading-[0.95]">
                Built for teams.<br />
                <span className="text-zinc-500">Not for solo heroes.</span>
              </h2>
              <p className="text-zinc-600 mt-6 text-lg leading-relaxed">
                Invite teammates into a company workspace. See every draft your colleagues are
                working on. Comment, @-mention, and approve right inside the editor.
              </p>
              <ul className="mt-8 space-y-3">
                {[
                  { icon: Buildings, label: "Org-scoped proposals — managers see everything" },
                  { icon: At, label: "@-mention review threads with email notifications" },
                  { icon: ChartLineUp, label: "Approval-rate reporting per writer" },
                  { icon: FileText, label: "Watermarked PDFs with submission references" },
                ].map((b) => (
                  <li key={b.label} className="flex items-start gap-3">
                    <div className="h-8 w-8 bg-zinc-100 border border-zinc-200 flex items-center justify-center shrink-0 mt-0.5">
                      <b.icon size={16} className="text-zinc-700" weight="bold" />
                    </div>
                    <span className="text-zinc-700 leading-relaxed">{b.label}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="border-2 border-zinc-900 bg-white p-6 lg:p-8">
              <div className="flex items-center justify-between border-b border-zinc-200 pb-3 mb-4">
                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Review thread · USDA Rural Broadband</div>
                <span className="text-[10px] uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 font-mono">Live</span>
              </div>
              <div className="space-y-4">
                <div className="border border-zinc-200 p-4">
                  <div className="flex items-baseline gap-2">
                    <div className="font-bold text-sm">Alice Chen</div>
                    <div className="text-[10px] font-mono text-zinc-500">2m ago</div>
                  </div>
                  <p className="mt-2 text-sm text-zinc-700">Draft is ready for review. Section 4.2 budget needs your eye <span className="bg-blue-50 text-blue-700 border border-blue-200 px-1.5 font-mono text-xs">@David</span></p>
                </div>
                <div className="border border-zinc-200 p-4 ml-6">
                  <div className="flex items-baseline gap-2">
                    <div className="font-bold text-sm">David Park</div>
                    <div className="text-[10px] font-mono text-zinc-500">just now</div>
                  </div>
                  <p className="mt-2 text-sm text-zinc-700">Looks solid. Approved — submit when ready.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =============================== Pricing =============================== */}
      <section id="pricing" className="border-b border-zinc-200 bg-zinc-50" data-testid="pricing">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="gf-overline">Pricing</div>
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4 leading-[0.95] max-w-3xl">
            Simple pricing. <span className="text-blue-700">Real value.</span>
          </h2>
          <p className="text-zinc-600 mt-6 text-lg max-w-2xl leading-relaxed">
            Start free for 7 days. When you&apos;re ready, pick a true monthly subscription,
            a one-time 30-day pass, or our Lifetime plan — every option unlocks every feature.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 mt-12">
            <div className="bg-white p-8 lg:p-10" data-testid="price-trial">
              <div className="gf-overline">Trial</div>
              <div className="font-display font-black text-5xl tracking-tight mt-3">Free</div>
              <div className="text-sm text-zinc-500 mt-1">7 days · No card required</div>
              <Link to="/register" data-testid="price-trial-cta" className="block mt-8 border-2 border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white font-bold uppercase tracking-widest px-6 py-3 text-xs text-center transition-colors">
                Start trial
              </Link>
              <ul className="mt-8 space-y-2 text-sm text-zinc-700">
                <li>✓ Full feature access</li>
                <li>✓ Up to 3 AI drafts</li>
                <li>✓ Email submission</li>
              </ul>
            </div>
            <div className="bg-white p-8 lg:p-10 border-y-4 lg:border-y-0 lg:border-x-4 border-blue-700 relative" data-testid="price-monthly">
              <div className="absolute top-0 right-0 bg-blue-700 text-white text-[10px] font-mono uppercase tracking-widest px-2 py-1">
                ★ Recommended
              </div>
              <div className="gf-overline text-blue-700">Monthly</div>
              <div className="font-display font-black text-5xl tracking-tight mt-3">$14.<span className="text-2xl">99</span><span className="text-base text-zinc-500 ml-1">/mo</span></div>
              <div className="text-sm text-zinc-500 mt-1">Auto-renews · Cancel anytime</div>
              <Link to="/register" data-testid="price-monthly-cta" className="block mt-8 bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-widest px-6 py-3 text-xs text-center">
                Subscribe monthly
              </Link>
              <ul className="mt-8 space-y-2 text-sm text-zinc-700">
                <li>✓ Unlimited AI drafts</li>
                <li>✓ Saved-search alerts</li>
                <li>✓ Multi-seat company workspace</li>
                <li>✓ Customer Portal</li>
              </ul>
            </div>
            <div className="bg-white p-8 lg:p-10" data-testid="price-pass">
              <div className="gf-overline">30-day pass</div>
              <div className="font-display font-black text-5xl tracking-tight mt-3">$19.<span className="text-2xl">99</span></div>
              <div className="text-sm text-zinc-500 mt-1">One-time · No renewal</div>
              <Link to="/register" data-testid="price-pass-cta" className="block mt-8 border-2 border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white font-bold uppercase tracking-widest px-6 py-3 text-xs text-center transition-colors">
                Buy pass
              </Link>
              <ul className="mt-8 space-y-2 text-sm text-zinc-700">
                <li>✓ 30 days, all features</li>
                <li>✓ Stacks on existing time</li>
                <li>✓ No auto-charge</li>
              </ul>
            </div>
            <div className="bg-zinc-900 text-white p-8 lg:p-10 relative" data-testid="price-lifetime">
              <div className="absolute top-0 right-0 bg-amber-400 text-zinc-900 text-[10px] font-mono uppercase tracking-widest px-2 py-1">
                Best value
              </div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-amber-400">Lifetime</div>
              <div className="font-display font-black text-5xl tracking-tight mt-3">$199</div>
              <div className="text-sm text-zinc-400 mt-1">One-time · Never expires</div>
              <Link to="/register" data-testid="price-lifetime-cta" className="block mt-8 bg-amber-400 hover:bg-amber-500 text-zinc-900 font-bold uppercase tracking-widest px-6 py-3 text-xs text-center">
                Buy lifetime
              </Link>
              <ul className="mt-8 space-y-2 text-sm text-zinc-200">
                <li>✓ Forever premium — no resub</li>
                <li>✓ All future features</li>
                <li>✓ Pays for itself in ~14 months</li>
              </ul>
            </div>
          </div>

          {/* Founder's Plan — separate call-out block below the main pricing grid.
              Only visible while slots remain (auto-hides on sold-out). */}
          {founder && !founder.sold_out && (
            <div className="mt-6 border-2 border-amber-500 bg-white p-6 lg:p-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6" data-testid="price-founder">
              <div className="flex items-start gap-4 flex-1">
                <div className="hidden sm:flex h-12 w-12 shrink-0 bg-amber-500 items-center justify-center">
                  <Star size={22} weight="fill" className="text-zinc-900" />
                </div>
                <div>
                  <div className="text-[11px] font-mono uppercase tracking-widest text-amber-600">
                    Founder&apos;s Plan · Locked for life
                  </div>
                  <div className="font-display font-black text-3xl lg:text-4xl tracking-tight mt-1">
                    <span className="text-xl lg:text-2xl text-zinc-400 line-through font-bold mr-2" data-testid="price-founder-old-price">$9.99</span>
                    ${founder.founder_price_usd.toFixed(2)}
                    <span className="text-base text-zinc-500 ml-1 font-normal">/month, forever</span>
                  </div>
                  <p className="text-sm text-zinc-600 mt-2 max-w-md">
                    A thank-you for backing GrantFlow in year one. The <b>${founder.founder_price_usd.toFixed(2)}/mo</b> rate never
                    increases for as long as you stay subscribed. Available to the first{" "}
                    <b>{founder.founder_limit}</b> subscribers only.
                    {founder.lifetime_price_usd ? (
                      <span className="block mt-1">
                        Prefer one payment? <b>${founder.lifetime_price_usd.toFixed(0)} once</b> = lifetime access, same first-100 pool.
                      </span>
                    ) : null}
                  </p>
                </div>
              </div>
              <div className="w-full lg:w-auto flex flex-col items-start lg:items-end gap-2">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                  Spots remaining
                </div>
                <div className="font-display font-black text-4xl tracking-tight text-amber-600">
                  {founder.founder_remaining}<span className="text-lg text-zinc-400 font-normal">/{founder.founder_limit}</span>
                </div>
                <Link
                  to="/register"
                  data-testid="price-founder-cta"
                  className="bg-amber-500 hover:bg-amber-600 text-zinc-900 font-bold uppercase tracking-widest px-6 py-3 text-xs inline-flex items-center gap-2"
                >
                  Claim founder pricing
                  <ArrowRight size={12} weight="bold" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ============================== Closing CTA ============================== */}
      <section className="bg-zinc-900 text-white" data-testid="closing-cta">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Stop managing spreadsheets</div>
              <h2 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter mt-6">
                Submit your<br />
                <span className="text-blue-400">next grant</span> tonight.
              </h2>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3">
              <Link to="/register" data-testid="closing-cta-primary" className="bg-white hover:bg-zinc-100 text-zinc-900 font-bold uppercase tracking-widest px-8 py-5 inline-flex items-center justify-center gap-2 text-sm">
                Start free trial <ArrowRight size={16} weight="bold" />
              </Link>
              <Link to="/login" data-testid="closing-cta-secondary" className="border-2 border-zinc-700 text-zinc-300 hover:bg-zinc-800 font-bold uppercase tracking-widest px-8 py-5 inline-flex items-center justify-center text-sm transition-colors">
                Sign in
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* =============================== Footer =============================== */}
      <footer className="border-t border-zinc-200 bg-zinc-50" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2.5">
                <div className="h-8 w-8 bg-blue-700 flex items-center justify-center">
                  <Buildings size={18} className="text-white" weight="bold" />
                </div>
                <div className="font-display font-black text-lg leading-none tracking-tight">GrantFlow</div>
              </div>
              <p className="text-sm text-zinc-600 mt-4 max-w-md leading-relaxed">
                The federal grants suite for organizations that value time, compliance, and a
                tight feedback loop between discovery and submission.
              </p>
            </div>
            <div>
              <div className="gf-overline">Product</div>
              <ul className="mt-4 space-y-2 text-sm">
                <li><Link to="/login" className="text-zinc-700 hover:text-blue-700">Sign in</Link></li>
                <li><Link to="/register" className="text-zinc-700 hover:text-blue-700">Start trial</Link></li>
                <li><a href="#pricing" className="text-zinc-700 hover:text-blue-700">Pricing</a></li>
              </ul>
            </div>
            <div>
              <div className="gf-overline">Support</div>
              <ul className="mt-4 space-y-2 text-sm">
                <li><a href="mailto:support@grantflow.it.com" className="text-zinc-700 hover:text-blue-700">support@grantflow.it.com</a></li>
                <li>
                  <Link
                    to="/legal/terms"
                    data-testid="landing-link-terms"
                    className="text-zinc-700 hover:text-blue-700"
                  >
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link
                    to="/legal/privacy"
                    data-testid="landing-link-privacy"
                    className="text-zinc-700 hover:text-blue-700"
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/legal/copyright"
                    data-testid="landing-link-copyright"
                    className="text-zinc-700 hover:text-blue-700"
                  >
                    Copyright & DMCA
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-6 border-t border-zinc-200 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
            <div className="text-xs font-mono text-zinc-500">
              © {new Date().getFullYear()} GrantFlow Inc. · All rights reserved.
            </div>
            <div className="text-xs font-mono text-zinc-500 flex items-center gap-2">
              <Lightning size={12} /> Powered by Grants.gov · Claude Sonnet 4.5 · Stripe · Resend
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
